import { LocaleText } from "./locales";
import * as texts from "./locales";

export interface ApiParam {
  name: string;
  in: "query" | "path" | "header";
  required: boolean;
  type: string;
  description: LocaleText;
}

export interface ResponseField {
  /** Dot/bracket path of the field, e.g. `evses[].connectors[].standard`. */
  path: string;
  type: string;
  /** Closed set of allowed values (enums), rendered as tags. */
  values?: string[];
  description: LocaleText;
}

export interface HeaderField {
  name: string;
  description: LocaleText;
}

export interface ErrorRow {
  code: string;
  description: LocaleText;
}

export interface ApiEndpoint {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  name: LocaleText;
  url: string;
  protocol: string;
  contentType: string;
  description: LocaleText;
  auth: LocaleText;
  params: ApiParam[];
  responseFields: ResponseField[];
  responseExample: string;
  paginationHeaders?: HeaderField[];
  accessControl?: LocaleText;
  notes?: LocaleText;
  errors: ErrorRow[];
  curl: string;
}

// ─── Builders (all translatable copy is referenced from ./locales) ──────────────

const f = (
  path: string,
  type: string,
  description: LocaleText,
  values?: string[],
): ResponseField => ({ path, type, values, description });

const p = (
  name: string,
  where: ApiParam["in"],
  required: boolean,
  type: string,
  description: LocaleText,
): ApiParam => ({ name, in: where, required, type, description });

const e = (code: string, description: LocaleText): ErrorRow => ({ code, description });

// ─── Enum value sets (mirrors tupi-ocpi-server DTOs/enums; not translatable) ────

export const CONNECTOR_STANDARDS = [
  "CHADEMO", "DOMESTIC_A", "DOMESTIC_B", "DOMESTIC_C", "DOMESTIC_D",
  "DOMESTIC_E", "DOMESTIC_F", "DOMESTIC_G", "DOMESTIC_H", "DOMESTIC_I",
  "DOMESTIC_J", "DOMESTIC_K", "DOMESTIC_L",
  "IEC_60309_2_single_16", "IEC_60309_2_three_16", "IEC_60309_2_three_32",
  "IEC_60309_2_three_64", "IEC_62196_T1", "IEC_62196_T1_COMBO",
  "IEC_62196_T2", "IEC_62196_T2_COMBO", "IEC_62196_T3A", "IEC_62196_T3C",
  "PANTOGRAPH_BOTTOM_UP", "PANTOGRAPH_TOP_DOWN", "TESLA_R", "TESLA_S",
  // 2.2.1 only
  "CHAOJI", "DOMESTIC_M", "DOMESTIC_N", "DOMESTIC_O", "GBT_AC", "GBT_DC",
  "NEMA_5_20", "NEMA_6_30", "NEMA_6_50", "NEMA_10_30", "NEMA_10_50",
  "NEMA_14_30", "NEMA_14_50",
];

export const CONNECTOR_FORMATS = ["CABLE", "SOCKET"];

export const POWER_TYPES = [
  "AC_1_PHASE", "AC_3_PHASE", "DC",
  // 2.2.1 only
  "AC_2_PHASE", "AC_2_PHASE_SPLIT",
];

export const EVSE_STATUSES = [
  "AVAILABLE", "BLOCKED", "CHARGING", "INOPERATIVE", "OUTOFORDER",
  "PLANNED", "REMOVED", "RESERVED", "UNKNOWN",
];

export const EVSE_CAPABILITIES = [
  "CHARGING_PROFILE_CAPABLE", "CHARGING_PREFERENCES_CAPABLE",
  "CHIP_CARD_SUPPORT", "CONTACTLESS_CARD_SUPPORT", "CREDIT_CARD_PAYABLE",
  "DEBIT_CARD_PAYABLE", "PED_TERMINAL", "REMOTE_START_STOP_CAPABLE",
  "RESERVABLE", "RFID_READER", "START_SESSION_CONNECTOR_REQUIRED",
  "TOKEN_GROUP_CAPABLE", "UNLOCK_CAPABLE",
];

export const PARKING_RESTRICTIONS = [
  "EV_ONLY", "PLUGGED", "DISABLED", "CUSTOMERS", "MOTORCYCLES",
];

export const PARKING_TYPES = [
  "ALONG_MOTORWAY", "PARKING_GARAGE", "PARKING_LOT",
  "ON_DRIVEWAY", "ON_STREET", "UNDERGROUND_GARAGE",
];

export const SESSION_STATUSES = [
  "ACTIVE", "COMPLETED", "INVALID", "PENDING", "RESERVATION",
];

export const AUTH_METHODS = ["AUTH_REQUEST", "COMMAND", "WHITELIST"];

export const CDR_DIMENSION_TYPES = [
  "CURRENT", "ENERGY", "ENERGY_EXPORT", "ENERGY_IMPORT", "MAX_CURRENT",
  "MIN_CURRENT", "MAX_POWER", "MIN_POWER", "PARKING_TIME", "POWER",
  "RESERVATION_TIME", "STATE_OF_CHARGE", "TIME",
];

const OCPI_STATUS_CODES = ["1000", "2000", "2001", "2002", "2003", "2004", "3000", "3001"];

// ─── Shared building blocks ─────────────────────────────────────────────────────

const TOKEN_PARAM = p("Authorization", "header", true, "string", texts.DOC_PARAM_AUTH_DESC);
const VERSION_PARAM = p("version", "path", true, "2.2.1 | 2.2", texts.DOC_PARAM_VERSION_DESC);

const OCPI_PAGINATION_PARAMS: ApiParam[] = [
  p("limit", "query", false, "integer > 0", texts.DOC_PARAM_LIMIT_DESC),
  p("offset", "query", false, "integer ≥ 0", texts.DOC_PARAM_OFFSET_DESC),
  p("date_from", "query", false, "ISO 8601 datetime", texts.DOC_PARAM_DATE_FROM_DESC),
  p("date_to", "query", false, "ISO 8601 datetime", texts.DOC_PARAM_DATE_TO_DESC),
];

const ENVELOPE_FIELDS: ResponseField[] = [
  f("status_code", "integer", texts.DOC_ENV_STATUS_CODE, OCPI_STATUS_CODES),
  f("status_message", "string", texts.DOC_ENV_STATUS_MESSAGE),
  f("timestamp", "RFC 3339 datetime", texts.DOC_ENV_TIMESTAMP),
];

const OCPI_PAGINATION_HEADERS: HeaderField[] = [
  { name: "Link", description: texts.DOC_HDR_LINK },
  { name: "X-Total-Count", description: texts.DOC_HDR_TOTAL_COUNT },
  { name: "X-Limit", description: texts.DOC_HDR_LIMIT },
];

// HTTP status paired with the OCPI status_code returned in the envelope.
const OCPI_LIST_ERRORS: ErrorRow[] = [
  e("400 / OCPI 2001", texts.DOC_ERR_400),
  e("401 / OCPI 2001", texts.DOC_ERR_401),
  e("422 / OCPI 2001", texts.DOC_ERR_422),
  e("4xx / OCPI 2000", texts.DOC_ERR_4XX),
  e("5xx / OCPI 3000", texts.DOC_ERR_5XX),
];

// ─── Endpoint definitions ───────────────────────────────────────────────────────

export const LOCATIONS_ENDPOINT: ApiEndpoint = {
  method: "GET",
  name: texts.GET_LOCATIONS_NAME,
  url: "https://ocpi.tupinrg.app/{version}/locations",
  protocol: "OCPI 2.2.1 / 2.2",
  contentType: "application/json",
  description: texts.GET_LOCATIONS_DESCRIPTION,
  auth: texts.DOC_AUTH_TOKEN,
  params: [TOKEN_PARAM, VERSION_PARAM, ...OCPI_PAGINATION_PARAMS],
  responseFields: [
    f("data[]", "Location[]", texts.DOC_LOC_DATA),
    f("data[].id", "string", texts.DOC_LOC_ID),
    f("data[].country_code", "string", texts.DOC_LOC_COUNTRY_CODE),
    f("data[].party_id", "string", texts.DOC_LOC_PARTY_ID),
    f("data[].publish", "boolean", texts.DOC_LOC_PUBLISH),
    f("data[].name", "string?", texts.DOC_LOC_NAME),
    f("data[].address", "string?", texts.DOC_LOC_ADDRESS),
    f("data[].city", "string?", texts.DOC_LOC_CITY),
    f("data[].country", "string", texts.DOC_LOC_COUNTRY),
    f("data[].coordinates.latitude", "string", texts.DOC_LOC_LAT),
    f("data[].coordinates.longitude", "string", texts.DOC_LOC_LON),
    f("data[].parking_type", "enum?", texts.DOC_LOC_PARKING_TYPE, PARKING_TYPES),
    f("data[].operator", "object?", texts.DOC_LOC_OPERATOR),
    f("data[].suboperator", "object?", texts.DOC_LOC_SUBOPERATOR),
    f("data[].owner", "object?", texts.DOC_LOC_OWNER),
    f("data[].time_zone", "string", texts.DOC_LOC_TIMEZONE),
    f("data[].last_updated", "datetime", texts.DOC_LOC_LAST_UPDATED),
    f("data[].evses[]", "EVSE[]", texts.DOC_LOC_EVSES),
    f("data[].evses[].uid", "string", texts.DOC_EVSE_UID),
    f("data[].evses[].evse_id", "string?", texts.DOC_EVSE_ID),
    f("data[].evses[].status", "enum", texts.DOC_EVSE_STATUS, EVSE_STATUSES),
    f("data[].evses[].capabilities[]", "enum[]", texts.DOC_EVSE_CAPABILITIES, EVSE_CAPABILITIES),
    f("data[].evses[].parking_restrictions[]", "enum[]", texts.DOC_EVSE_PARKING_RESTR, PARKING_RESTRICTIONS),
    f("data[].evses[].last_updated", "datetime", texts.DOC_EVSE_LAST_UPDATED),
    f("data[].evses[].connectors[]", "Connector[]", texts.DOC_EVSE_CONNECTORS),
    f("data[].evses[].connectors[].id", "string", texts.DOC_CONN_ID),
    f("data[].evses[].connectors[].standard", "enum", texts.DOC_CONN_STANDARD, CONNECTOR_STANDARDS),
    f("data[].evses[].connectors[].format", "enum", texts.DOC_CONN_FORMAT, CONNECTOR_FORMATS),
    f("data[].evses[].connectors[].power_type", "enum", texts.DOC_CONN_POWER_TYPE, POWER_TYPES),
    f("data[].evses[].connectors[].max_voltage", "integer", texts.DOC_CONN_MAX_VOLTAGE),
    f("data[].evses[].connectors[].max_amperage", "integer", texts.DOC_CONN_MAX_AMPERAGE),
    f("data[].evses[].connectors[].max_electric_power", "integer?", texts.DOC_CONN_MAX_POWER),
    f("data[].evses[].connectors[].tariff_ids[]", "string[]", texts.DOC_CONN_TARIFF_IDS),
    f("data[].evses[].connectors[].last_updated", "datetime", texts.DOC_CONN_LAST_UPDATED),
    ...ENVELOPE_FIELDS,
  ],
  responseExample: `{
  "data": [
    {
      "id": "BR-TUP-LOC-001",
      "country_code": "BR",
      "party_id": "TUP",
      "publish": true,
      "name": "Shopping Mall A",
      "address": "Av. Paulista, 1000",
      "city": "São Paulo",
      "country": "BRA",
      "coordinates": { "latitude": "-23.56168", "longitude": "-46.65598" },
      "parking_type": "PARKING_GARAGE",
      "time_zone": "America/Sao_Paulo",
      "evses": [
        {
          "uid": "EVSE-001",
          "evse_id": "BR*TUP*E*EVSE001",
          "status": "AVAILABLE",
          "capabilities": ["RFID_READER"],
          "parking_restrictions": [],
          "connectors": [
            {
              "id": "1",
              "standard": "IEC_62196_T2",
              "format": "CABLE",
              "power_type": "AC_3_PHASE",
              "max_voltage": 400,
              "max_amperage": 32,
              "max_electric_power": 22000,
              "tariff_ids": ["TUPI_AC22"],
              "last_updated": "2024-07-01T12:00:00Z"
            }
          ],
          "last_updated": "2024-07-01T12:00:00Z"
        }
      ],
      "last_updated": "2024-07-01T12:00:00Z"
    }
  ],
  "status_code": 1000,
  "status_message": "Success",
  "timestamp": "2024-07-01T12:00:00Z"
}`,
  paginationHeaders: OCPI_PAGINATION_HEADERS,
  accessControl: texts.DOC_AC_LOCATIONS,
  errors: OCPI_LIST_ERRORS,
  curl: `curl -s \\\n  -H "Authorization: Token <token>" \\\n  "https://ocpi.tupinrg.app/2.2.1/locations?limit=100&offset=0&date_from=2024-01-01T00:00:00Z&date_to=2024-12-31T23:59:59Z"`,
};

export const SESSIONS_ENDPOINT: ApiEndpoint = {
  method: "GET",
  name: texts.GET_SESSIONS_NAME,
  url: "https://ocpi.tupinrg.app/{version}/sessions",
  protocol: "OCPI 2.2.1 / 2.2",
  contentType: "application/json",
  description: texts.GET_SESSIONS_DESCRIPTION,
  auth: texts.DOC_AUTH_TOKEN,
  params: [TOKEN_PARAM, VERSION_PARAM, ...OCPI_PAGINATION_PARAMS],
  responseFields: [
    f("data[]", "Session[]", texts.DOC_SES_DATA),
    f("data[].id", "string", texts.DOC_SES_ID),
    f("data[].country_code", "string", texts.DOC_SES_COUNTRY_CODE),
    f("data[].party_id", "string", texts.DOC_SES_PARTY_ID),
    f("data[].currency", "string", texts.DOC_SES_CURRENCY),
    f("data[].start_date_time", "datetime", texts.DOC_SES_START),
    f("data[].end_date_time", "datetime?", texts.DOC_SES_END),
    f("data[].kwh", "number", texts.DOC_SES_KWH),
    f("data[].status", "enum", texts.DOC_SES_STATUS, SESSION_STATUSES),
    f("data[].auth_method", "enum", texts.DOC_SES_AUTH_METHOD, AUTH_METHODS),
    f("data[].authorization_reference", "string?", texts.DOC_SES_AUTH_REF),
    f("data[].total_cost.excl_vat", "number", texts.DOC_SES_COST_EXCL),
    f("data[].total_cost.incl_vat", "number?", texts.DOC_SES_COST_INCL),
    f("data[].meter_id", "string?", texts.DOC_SES_METER_ID),
    f("data[].location_id", "string", texts.DOC_SES_LOCATION_ID),
    f("data[].evse_uid", "string", texts.DOC_SES_EVSE_UID),
    f("data[].connector_id", "string", texts.DOC_SES_CONNECTOR_ID),
    f("data[].charging_periods[]", "ChargingPeriod[]", texts.DOC_SES_PERIODS),
    f("data[].charging_periods[].start_date_time", "datetime", texts.DOC_SES_PERIOD_START),
    f("data[].charging_periods[].dimensions[]", "CdrDimension[]", texts.DOC_SES_DIMENSIONS),
    f("data[].charging_periods[].dimensions[].type", "enum", texts.DOC_SES_DIM_TYPE, CDR_DIMENSION_TYPES),
    f("data[].charging_periods[].dimensions[].volume", "number", texts.DOC_SES_DIM_VOLUME),
    f("data[].charging_periods[].tariff_id", "string?", texts.DOC_SES_TARIFF_ID),
    f("data[].last_updated", "datetime", texts.DOC_SES_LAST_UPDATED),
    ...ENVELOPE_FIELDS,
  ],
  responseExample: `{
  "data": [
    {
      "id": "BR-TUP-SESS-0001",
      "country_code": "BR",
      "party_id": "TUP",
      "currency": "BRL",
      "start_date_time": "2024-07-01T10:00:00Z",
      "end_date_time": "2024-07-01T11:15:00Z",
      "kwh": 12.34,
      "status": "COMPLETED",
      "auth_method": "WHITELIST",
      "authorization_reference": "ABC123456789",
      "total_cost": { "excl_vat": 22.5, "incl_vat": 24.75 },
      "meter_id": "MTR-001",
      "location_id": "BR-TUP-LOC-001",
      "evse_uid": "EVSE-001",
      "connector_id": "1",
      "charging_periods": [
        {
          "start_date_time": "2024-07-01T10:00:00Z",
          "dimensions": [
            { "type": "ENERGY", "volume": 12.34 },
            { "type": "TIME", "volume": 75 }
          ],
          "tariff_id": "TUPI_AC22"
        }
      ],
      "last_updated": "2024-07-01T11:15:05Z"
    }
  ],
  "status_code": 1000,
  "status_message": "Success",
  "timestamp": "2024-07-01T11:15:05Z"
}`,
  paginationHeaders: OCPI_PAGINATION_HEADERS,
  accessControl: texts.DOC_AC_SESSIONS,
  notes: texts.DOC_NOTE_SESSIONS,
  errors: OCPI_LIST_ERRORS,
  curl: `curl -s \\\n  -H "Authorization: Token <token>" \\\n  "https://ocpi.tupinrg.app/2.2.1/sessions?limit=100&offset=0&date_from=2024-01-01T00:00:00Z&date_to=2024-12-31T23:59:59Z"`,
};

export const USER_DATA_ENDPOINT: ApiEndpoint = {
  method: "GET",
  name: texts.GET_SESSION_USER_NAME,
  url: "https://ocpi.tupinrg.app/extra/v1/sessions/{country_code}/{party_id}/{session_id}/user-data",
  protocol: "OCPI 2.2.1 envelope + Tupi Extra v1",
  contentType: "application/json",
  description: texts.GET_SESSION_USER_DESCRIPTION,
  auth: texts.DOC_AUTH_TOKEN,
  params: [
    p("Authorization", "header", true, "string", texts.DOC_PARAM_AUTH_DESC),
    p("country_code", "path", true, "string", texts.DOC_PARAM_UD_COUNTRY_DESC),
    p("party_id", "path", true, "string", texts.DOC_PARAM_UD_PARTY_DESC),
    p("session_id", "path", true, "string", texts.DOC_PARAM_UD_SESSION_DESC),
  ],
  responseFields: [
    f("data.session_id", "string", texts.DOC_USR_SESSION_ID),
    f("data.user_data.name", "string", texts.DOC_USR_NAME),
    f("data.user_data.document", "string", texts.DOC_USR_DOCUMENT),
    f("data.user_data.email", "string", texts.DOC_USR_EMAIL),
    f("data.user_data.phone", "string", texts.DOC_USR_PHONE),
    f("data.user_data.street_name", "string", texts.DOC_USR_STREET),
    f("data.user_data.number", "string", texts.DOC_USR_NUMBER),
    f("data.user_data.district", "string", texts.DOC_USR_DISTRICT),
    f("data.user_data.city", "string", texts.DOC_USR_CITY),
    f("data.user_data.state", "string", texts.DOC_USR_STATE),
    f("data.user_data.zip_code", "string", texts.DOC_USR_ZIP),
    f("data.user_data.cars[]", "Car[]?", texts.DOC_USR_CARS),
    f("data.user_data.cars[].model", "string", texts.DOC_USR_CAR_MODEL),
    f("data.user_data.cars[].brand", "string", texts.DOC_USR_CAR_BRAND),
    f("data.user_data.cars[].capacity", "number", texts.DOC_USR_CAR_CAPACITY),
    f("data.user_data.cars[].autonomy", "number", texts.DOC_USR_CAR_AUTONOMY),
    f("data.coupon", "Coupon?", texts.DOC_USR_COUPON),
    f("data.coupon.name", "string", texts.DOC_USR_COUPON_NAME),
    f("data.coupon.type", "string", texts.DOC_USR_COUPON_TYPE),
    f("data.coupon.code", "string", texts.DOC_USR_COUPON_CODE),
    f(
      "data.coupon.gross_discount_value",
      "number",
      texts.DOC_USR_COUPON_DISCOUNT
    ),
    ...ENVELOPE_FIELDS,
  ],
  responseExample: `{
  "data": {
    "session_id": "123123",
    "user_data": {
      "name": "Maria Silva",
      "document": "12345678901",
      "email": "maria.silva@example.com",
      "phone": "+5511999998888",
      "street_name": "Av. Paulista",
      "number": "1000",
      "district": "Bela Vista",
      "city": "São Paulo",
      "state": "SP",
      "zip_code": "01310-100",
      "cars": [
        { "model": "Model 3", "brand": "Tesla", "capacity": 60, "autonomy": 491 }
      ]
    },
    "coupon": {
      "name": "Campanha Recarga Verde",
      "type": "PERCENTAGE",
      "code": "VERDE10",
      "gross_discount_value": 12.5
    }
  },
  "status_code": 1000,
  "status_message": "Success",
  "timestamp": "2024-07-01T12:00:00Z"
}`,
  errors: [
    e("401 / OCPI 2001", texts.DOC_ERR_UD_401),
    e("404 / OCPI 2000", texts.DOC_ERR_UD_404),
    e("422 / OCPI 2001", texts.DOC_ERR_422),
    e("4xx / OCPI 2000", texts.DOC_ERR_4XX),
    e("5xx / OCPI 3000", texts.DOC_ERR_5XX),
  ],
  curl: `curl -s \\\n  -H "Authorization: Token <token>" \\\n  "https://ocpi.tupinrg.app/extra/v1/sessions/BR/XYZ/123123/user-data"`,
};

export const API_DOCS: ApiEndpoint[] = [
  LOCATIONS_ENDPOINT,
  SESSIONS_ENDPOINT,
  USER_DATA_ENDPOINT,
];
